import { map, catchError } from 'rxjs/operators';
import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SessionStorageService } from '../services/session-storage.service';
import { EnvironmentService } from '../services/environment.service';
import { ErrorReportingService } from '../services/error-reporting.service';
import { RightBridgeApiService } from '../services/right-bridge-api.service';
import {
  CaseFile,
  CaseManagement,
  CaseManagementData,
  RecordNote,
} from '../models/case-management-model';
import {
  DynamicResponse,
  GridResults,
  StringResult,
} from '../models/general-models';
import { Observable } from 'rxjs';
import { RepData, UserData } from '../models/admin-models';

@Injectable({ providedIn: 'root' })
export class CaseManagementService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private envSvc = inject(EnvironmentService);
  private rbs = inject(RightBridgeApiService);
  private errSvc = inject(ErrorReportingService);

  private environment;
  public baseUrl: string;
  public f5500BaseUrl: string;
  private org: string;
  modalPreparing = signal(false);

  constructor() {
    this.environment = this.envSvc.get();

    if (!this.environment) {
      this.envSvc.loadSettings();
      this.environment = this.envSvc.get();
    }

    this.f5500BaseUrl = this.environment.f5500Base;
    this.baseUrl = this.environment.apiBase;
    this.org = this.environment.org;
    this.org = this.environment.org || 'basev2';
  }

  createCaseManagementRecord(id?, data?): Observable<StringResult> {
    let queryData = {};
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');

    let url = this.baseUrl + `/CaseManagement/Profile`;

    if (id) {
      url = url + `?RBProfileID=${id}`;
    }

    if (data) {
      queryData = data;
    }

    return this.http
      .post<StringResult>(url, queryData, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  processCaseManagementRecord(
    id
  ): Observable<DynamicResponse<'result', string>> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = this.baseUrl + `/CaseManagement/Process?profileId=${id}`;

    return this.http
      .post<DynamicResponse<'result', string>>(url, null, {
        headers: reqHeaders,
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getCaseManagementData(
    data,
    responseType = 'application/json'
  ): Observable<GridResults | Blob> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org)
      .set('Accept', responseType);
    const url = this.baseUrl + '/CaseManagement/Search';

    if (responseType === 'application/xlsx') {
      return this.http
        .post(url, data, {
          headers: reqHeaders,
          observe: 'response',
          responseType: 'blob',
        })
        .pipe(
          map(res => {
            return res.body as Blob;
          }),
          catchError((error: any) => {
            return this.errSvc.handleError(error);
          })
        );
    } else {
      return this.http
        .post<GridResults>(url, data, { headers: reqHeaders })
        .pipe(
          map(res => {
            return res;
          }),
          catchError(error => {
            return this.errSvc.handleError(error);
          })
        );
    }
  }

  saveCaseManagementRecord(id, data): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/CaseManagement/Profile?CRID=${id}`;

    return this.http.patch<void>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getCaseManagementUsers(repCode): Observable<UserData> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      `/CaseManagement/Users?repcode=${repCode}&rights=CaseManager`;

    return this.http.get<UserData>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  listCaseRecordFiles(id): Observable<CaseFile> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/CaseManagement/ListFiles?caseRecordId=${id}`;

    return this.http.get<CaseFile>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getCaseRecordFile(id): Observable<Blob> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/CaseManagement/GetFile?caseRecordFileId=${id}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body as Blob;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  saveCaseRecordFile(
    id,
    data,
    fileName?,
    fileType?
  ): Observable<{ id: string }> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    let url = this.baseUrl + `/CaseManagement/UploadFile?caseRecordId=${id}`;

    if (fileName) {
      url += `&name=${fileName}`;
    }
    if (fileType) {
      url += `&type=${fileType}`;
    }
    return this.http
      .post<{ id: string }>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  deleteCase(crid: string): Observable<{ status: string }> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/CaseManagement/delete?crid=${crid}`;

    return this.http
      .delete<{ status: string }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  deleteCaseRecordFile(id): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl + `/CaseManagement/DeleteFile?caseRecordFileId=${id}`;

    return this.http.delete<void>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getCaseRecordNotes(id): Observable<RecordNote[]> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/CaseManagement/GetNotes?caseRecordId=${id}`;

    return this.http.get<RecordNote[]>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  addCaseRecordNote(data): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const url = this.baseUrl + '/CaseManagement/AddNote';

    return this.http.post<void>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  editCaseRecordNote(data): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const url = this.baseUrl + '/CaseManagement/EditNote';

    return this.http.post<void>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getCaseManagementProfileData(id, groupName): Observable<CaseManagementData> {
    const sessId = this.ss.get('session');
    const headers = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    return this.rbs.getRequest<CaseManagementData>(
      `${this.rbs.baseUrl}/CaseManagement/Profile?CRID=${id}&groupName=${groupName}`,
      headers
    );
  }

  getCmUserFilterList(
    searchString?: string
  ): Observable<{ Users: UserData[] }> {
    this.modalPreparing.set(true);
    const sessId = this.ss.get('session');
    const headers = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    let url = `${this.rbs.baseUrl}/CaseManagement/UserFilterList`;

    if (searchString) {
      url = url + `?userid=${searchString}`;
    }

    return this.rbs.getRequest<{ Users: UserData[] }>(url, headers);
  }

  getCmRepCodes(searchString, maxRows = 20): Observable<{ names: RepData[] }> {
    const sessId = this.ss.get('session');
    const headers = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    let url = `${this.rbs.baseUrl}/Hierarchy/RepCode`;

    if (searchString) {
      url = url + `?searchString=${searchString}&maxRows=${maxRows}`;
    }

    return this.rbs.getRequest<{ names: RepData[] }>(url, headers);
  }

  getRelatedCases(id, caseType, app = 'cm'): Observable<CaseManagement[]> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      `/CaseManagement/RelatedCases?id=${id}&caseType=${caseType}`;

    return this.http.get<CaseManagement[]>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getCaseNotesAndFiles(ids): Observable<{ NoteId: string }[]> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'cm')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/CaseManagement/NotesAndFiles?caseids=${ids}`;

    return this.http
      .get<{ NoteId: string }[]>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  setModalPreparing(value: boolean): void {
    this.modalPreparing.set(value);
  }
}
